import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Monitor, Palette, Search, Headphones, ShoppingCart, 
  Smartphone, Settings, Globe, ArrowRight, CheckCircle 
} from 'lucide-react';
import { SEO, ServiceSchema } from '@/components/seo/SEO';
import { useLanguage } from '@/context/LanguageContext';

const ServicesPage = () => {
  const { language, getCmsContent, cmsLoaded, t } = useLanguage();

  const icons = [Monitor, ShoppingCart, Palette, Search, Smartphone, Headphones, Settings, Globe];

  // Translated services
  const translatedServices = {
    nl: [
      {
        icon: Monitor,
        title: 'Website op maat',
        description: 'Een unieke website die perfect aansluit bij jouw bedrijf en doelgroep. Volledig responsive en geoptimaliseerd voor alle apparaten.',
        features: ['Uniek design', 'Mobielvriendelijk', 'Snelle laadtijd', 'Contactformulier'],
      },
      {
        icon: ShoppingCart,
        title: 'Webshop ontwikkeling',
        description: 'Professionele webshop waar je direct mee kunt verkopen. Inclusief betaalmogelijkheden en voorraadbeheer.',
        features: ['Productbeheer', 'Veilig betalen', 'Voorraadbeheer', 'Orderverwerking'],
      },
      {
        icon: Palette,
        title: 'Professioneel ontwerp',
        description: 'Modern en aantrekkelijk design dat vertrouwen wekt bij je bezoekers en je merk versterkt.',
        features: ['Huisstijl integratie', 'Moderne uitstraling', 'Gebruiksvriendelijk', 'Consistente branding'],
      },
      {
        icon: Search,
        title: 'SEO optimalisatie',
        description: 'Gevonden worden in Google. Wij zorgen voor de technische SEO-basis zodat klanten je kunnen vinden.',
        features: ['Keyword onderzoek', 'Technische SEO', 'Snelheid optimalisatie', 'Meta tags'],
      },
      {
        icon: Smartphone,
        title: 'Mobile-first design',
        description: 'Websites die perfect werken op smartphones en tablets. Meer dan 60% van het verkeer komt van mobiel.',
        features: ['Touch-vriendelijk', 'Snelle mobiele ervaring', 'Responsive images', 'App-achtige navigatie'],
      },
      {
        icon: Headphones,
        title: 'Support & onderhoud',
        description: 'Na oplevering staan we voor je klaar. Updates, aanpassingen en technische support wanneer je het nodig hebt.',
        features: ['Maandelijks onderhoud', 'Beveiligingsupdates', 'Content wijzigingen', 'Technische support'],
      },
      {
        icon: Settings,
        title: 'Technisch beheer',
        description: 'Domein, hosting en e-mail volledig geregeld. Geen gedoe met technische zaken.',
        features: ['Domeinregistratie', 'Betrouwbare hosting', 'SSL-certificaat', 'Dagelijkse backups'],
      },
      {
        icon: Globe,
        title: 'Meertalige websites',
        description: 'Bereik klanten wereldwijd met een meertalige website. Nederlands, Engels, Frans, Duits en meer.',
        features: ['Taalversies', 'Automatische detectie', 'SEO per taal', 'Eenvoudig beheer'],
      },
    ],
    en: [
      {
        icon: Monitor,
        title: 'Custom website',
        description: 'A unique website that perfectly matches your business and target audience. Fully responsive and optimized for all devices.',
        features: ['Unique design', 'Mobile-friendly', 'Fast loading', 'Contact form'],
      },
      {
        icon: ShoppingCart,
        title: 'Webshop development',
        description: 'Professional webshop where you can start selling immediately. Including payment options and inventory management.',
        features: ['Product management', 'Secure payments', 'Inventory management', 'Order processing'],
      },
      {
        icon: Palette,
        title: 'Professional design',
        description: 'Modern and attractive design that builds trust with your visitors and strengthens your brand.',
        features: ['Brand integration', 'Modern look', 'User-friendly', 'Consistent branding'],
      },
      {
        icon: Search,
        title: 'SEO optimization',
        description: 'Get found on Google. We take care of the technical SEO foundation so customers can find you.',
        features: ['Keyword research', 'Technical SEO', 'Speed optimization', 'Meta tags'],
      },
      {
        icon: Smartphone,
        title: 'Mobile-first design',
        description: 'Websites that work perfectly on smartphones and tablets. More than 60% of traffic comes from mobile.',
        features: ['Touch-friendly', 'Fast mobile experience', 'Responsive images', 'App-like navigation'],
      },
      {
        icon: Headphones,
        title: 'Support & maintenance',
        description: 'After delivery, we\'re here for you. Updates, modifications, and technical support when you need it.',
        features: ['Monthly maintenance', 'Security updates', 'Content changes', 'Technical support'],
      },
      {
        icon: Settings,
        title: 'Technical management',
        description: 'Domain, hosting, and email fully managed. No hassle with technical matters.',
        features: ['Domain registration', 'Reliable hosting', 'SSL certificate', 'Daily backups'],
      },
      {
        icon: Globe,
        title: 'Multilingual websites',
        description: 'Reach customers worldwide with a multilingual website. Dutch, English, French, German and more.',
        features: ['Language versions', 'Automatic detection', 'SEO per language', 'Easy management'],
      },
    ],
    fr: [
      {
        icon: Monitor,
        title: 'Site web sur mesure',
        description: 'Un site web unique qui correspond parfaitement à votre entreprise et à votre public cible.',
        features: ['Design unique', 'Mobile-friendly', 'Chargement rapide', 'Formulaire de contact'],
      },
      {
        icon: ShoppingCart,
        title: 'Développement de boutique',
        description: 'Boutique en ligne professionnelle où vous pouvez commencer à vendre immédiatement.',
        features: ['Gestion des produits', 'Paiements sécurisés', 'Gestion des stocks', 'Traitement des commandes'],
      },
      {
        icon: Palette,
        title: 'Design professionnel',
        description: 'Design moderne et attrayant qui inspire confiance à vos visiteurs et renforce votre marque.',
        features: ['Intégration de marque', 'Look moderne', 'Convivial', 'Branding cohérent'],
      },
      {
        icon: Search,
        title: 'Optimisation SEO',
        description: 'Soyez trouvé sur Google. Nous nous occupons de la base technique SEO.',
        features: ['Recherche de mots-clés', 'SEO technique', 'Optimisation vitesse', 'Balises méta'],
      },
      {
        icon: Smartphone,
        title: 'Design mobile-first',
        description: 'Sites web qui fonctionnent parfaitement sur smartphones et tablettes.',
        features: ['Touch-friendly', 'Expérience mobile rapide', 'Images responsives', 'Navigation app-like'],
      },
      {
        icon: Headphones,
        title: 'Support & maintenance',
        description: 'Après livraison, nous sommes là pour vous. Mises à jour et support technique.',
        features: ['Maintenance mensuelle', 'Mises à jour sécurité', 'Modifications contenu', 'Support technique'],
      },
      {
        icon: Settings,
        title: 'Gestion technique',
        description: 'Domaine, hébergement et e-mail entièrement gérés. Pas de tracas techniques.',
        features: ['Enregistrement domaine', 'Hébergement fiable', 'Certificat SSL', 'Sauvegardes quotidiennes'],
      },
      {
        icon: Globe,
        title: 'Sites multilingues',
        description: 'Atteignez des clients dans le monde entier avec un site multilingue.',
        features: ['Versions linguistiques', 'Détection automatique', 'SEO par langue', 'Gestion facile'],
      },
    ],
    de: [
      {
        icon: Monitor,
        title: 'Individuelle Website',
        description: 'Eine einzigartige Website, die perfekt zu Ihrem Unternehmen und Ihrer Zielgruppe passt.',
        features: ['Einzigartiges Design', 'Mobilfreundlich', 'Schnelle Ladezeit', 'Kontaktformular'],
      },
      {
        icon: ShoppingCart,
        title: 'Webshop-Entwicklung',
        description: 'Professioneller Webshop, in dem Sie sofort verkaufen können.',
        features: ['Produktverwaltung', 'Sichere Zahlungen', 'Bestandsverwaltung', 'Auftragsabwicklung'],
      },
      {
        icon: Palette,
        title: 'Professionelles Design',
        description: 'Modernes und attraktives Design, das Vertrauen bei Ihren Besuchern aufbaut.',
        features: ['Markenintegration', 'Moderner Look', 'Benutzerfreundlich', 'Konsistentes Branding'],
      },
      {
        icon: Search,
        title: 'SEO-Optimierung',
        description: 'Werden Sie bei Google gefunden. Wir kümmern uns um die technische SEO-Basis.',
        features: ['Keyword-Recherche', 'Technisches SEO', 'Geschwindigkeitsoptimierung', 'Meta-Tags'],
      },
      {
        icon: Smartphone,
        title: 'Mobile-First-Design',
        description: 'Websites, die perfekt auf Smartphones und Tablets funktionieren.',
        features: ['Touch-freundlich', 'Schnelle mobile Erfahrung', 'Responsive Bilder', 'App-ähnliche Navigation'],
      },
      {
        icon: Headphones,
        title: 'Support & Wartung',
        description: 'Nach der Lieferung sind wir für Sie da. Updates und technischer Support.',
        features: ['Monatliche Wartung', 'Sicherheitsupdates', 'Inhaltsänderungen', 'Technischer Support'],
      },
      {
        icon: Settings,
        title: 'Technische Verwaltung',
        description: 'Domain, Hosting und E-Mail vollständig verwaltet. Kein Ärger mit technischen Angelegenheiten.',
        features: ['Domain-Registrierung', 'Zuverlässiges Hosting', 'SSL-Zertifikat', 'Tägliche Backups'],
      },
      {
        icon: Globe,
        title: 'Mehrsprachige Websites',
        description: 'Erreichen Sie Kunden weltweit mit einer mehrsprachigen Website.',
        features: ['Sprachversionen', 'Automatische Erkennung', 'SEO pro Sprache', 'Einfache Verwaltung'],
      },
    ],
  };

  // Page translations
  const pageText = {
    nl: {
      heroTitle: 'Onze',
      heroHighlight: 'diensten',
      heroDescription: 'Van concept tot lancering: alles wat je nodig hebt voor een succesvolle online aanwezigheid.',
      ctaTitle: 'Weet je niet welke dienst je nodig hebt?',
      ctaDescription: 'Geen probleem! Neem contact met ons op en we helpen je graag de juiste oplossing te vinden.',
      ctaButton: 'Plan een gesprek',
      seoTitle: 'Onze Diensten - Webdesign, Webshops & SEO',
      seoDescription: 'Ontdek onze webdesign diensten: websites op maat, webshops, SEO optimalisatie, hosting en onderhoud.'
    },
    en: {
      heroTitle: 'Our',
      heroHighlight: 'services',
      heroDescription: 'From concept to launch: everything you need for a successful online presence.',
      ctaTitle: 'Not sure which service you need?',
      ctaDescription: 'No problem! Contact us and we\'ll help you find the right solution.',
      ctaButton: 'Schedule a call',
      seoTitle: 'Our Services - Web Design, Webshops & SEO',
      seoDescription: 'Discover our web design services: custom websites, webshops, SEO optimization, hosting and maintenance.'
    },
    fr: {
      heroTitle: 'Nos',
      heroHighlight: 'services',
      heroDescription: 'Du concept au lancement: tout ce dont vous avez besoin pour une présence en ligne réussie.',
      ctaTitle: 'Vous ne savez pas quel service vous avez besoin?',
      ctaDescription: 'Pas de problème! Contactez-nous et nous vous aiderons à trouver la bonne solution.',
      ctaButton: 'Planifier un appel',
      seoTitle: 'Nos Services - Web Design, Boutiques & SEO',
      seoDescription: 'Découvrez nos services: sites web sur mesure, boutiques, optimisation SEO, hébergement et maintenance.'
    },
    de: {
      heroTitle: 'Unsere',
      heroHighlight: 'Dienstleistungen',
      heroDescription: 'Vom Konzept bis zum Launch: alles, was Sie für eine erfolgreiche Online-Präsenz brauchen.',
      ctaTitle: 'Nicht sicher, welchen Service Sie brauchen?',
      ctaDescription: 'Kein Problem! Kontaktieren Sie uns und wir helfen Ihnen, die richtige Lösung zu finden.',
      ctaButton: 'Gespräch planen',
      seoTitle: 'Unsere Dienstleistungen - Webdesign, Webshops & SEO',
      seoDescription: 'Entdecken Sie unsere Webdesign-Dienstleistungen: individuelle Websites, Webshops, SEO-Optimierung.'
    }
  };

  const currentPageText = pageText[language] || pageText.nl;
  const defaultServices = translatedServices[language] || translatedServices.nl;

  // Get services - try CMS first, then fallback to defaults
  const getServices = () => {
    if (!cmsLoaded) return defaultServices;
    
    const cmsServices = [];
    for (let i = 1; i <= 8; i++) {
      const title = getCmsContent(`services.service_${i}.title`);
      const description = getCmsContent(`services.service_${i}.description`);
      const featuresStr = getCmsContent(`services.service_${i}.features`);
      
      if (title && description) {
        cmsServices.push({
          icon: icons[i - 1],
          title,
          description,
          features: featuresStr ? featuresStr.split('|') : defaultServices[i - 1].features,
        });
      }
    }
    
    return cmsServices.length === 8 ? cmsServices : defaultServices;
  };

  const services = getServices();

  // Get CMS content or fallback to translated text
  const heroTitle = getCmsContent('services.hero.title') || currentPageText.heroTitle;
  const heroTitleHighlight = getCmsContent('services.hero.titleHighlight') || currentPageText.heroHighlight;
  const heroDescription = getCmsContent('services.hero.description') || currentPageText.heroDescription;
  const ctaTitle = getCmsContent('services.cta.title') || currentPageText.ctaTitle;
  const ctaDescription = getCmsContent('services.cta.description') || currentPageText.ctaDescription;
  const ctaButton = getCmsContent('services.cta.button') || currentPageText.ctaButton;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <main data-testid="services-page">
      <SEO 
        title={currentPageText.seoTitle}
        description={currentPageText.seoDescription}
        keywords="webdesign diensten, website laten maken, webshop, SEO, hosting, onderhoud"
      />
      <ServiceSchema 
        name="Web Design Services"
        description="Professional web design and development services including custom websites, e-commerce, SEO and maintenance"
      />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#f8f9fc] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0f172a] mb-6">
              {heroTitle} <span className="text-gradient">{heroTitleHighlight}</span>
            </h1>
            <p className="text-xl text-[#475569]">
              {heroDescription}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8"
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                variants={itemVariants}
                className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-[#2563EB]/20 transition-all duration-300 group"
                data-testid={`service-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#2563EB] transition-colors">
                    <service.icon className="w-8 h-8 text-[#2563EB] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#0f172a] mb-3">{service.title}</h3>
                    <p className="text-[#475569] mb-4">{service.description}</p>
                    <ul className="grid grid-cols-2 gap-2">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-[#475569]">
                          <CheckCircle className="w-4 h-4 text-[#10b981] flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#f8f9fc]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-6">
              {ctaTitle}
            </h2>
            <p className="text-lg text-[#475569] mb-8">
              {ctaDescription}
            </p>
            <Link
              to="/contact"
              className="btn-accent inline-flex items-center gap-2"
              data-testid="services-cta"
            >
              {ctaButton}
              <ArrowRight size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
